import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { formatAbsoluteChange } from "@/utils/adaptation";
import { formatAbbreviatedValueWithMetricUnit } from "@/utils/number";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { EqualsIcon } from "@phosphor-icons/react";
import { computeAbsoluteChange, computeRelativeChange, isEffectivelyUnchanged, MetricKey, unitForMetric } from "./utils";
type HeaderProps = {
  title: string | JSX.Element | null;
  description?: JSX.Element | string | null;
  tag?: React.ReactNode;
};
export const Header = ({
  title,
  description,
  tag
}: HeaderProps) => {
  return <div className="flex items-start justify-between gap-2" data-sentry-component="Header" data-sentry-source-file="Text.tsx">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-semibold tracking-[-0.01em] text-gray-900 @sm/pointLevelWrapper:!text-base @2xl/pointLevelWrapper:!text-lg @2xl/wrapper:text-2xl">
          <span>{title}</span> {tag}
        </h2>
        {description && <div className="text-base text-gray-500 @2xl/pointLevelWrapper:!text-base @2xl/wrapper:text-lg">
            {description}
          </div>}
      </div>
    </div>;
};
type ChartHeaderProps = {
  title: string | JSX.Element | null;
  description?: JSX.Element | string | null;
  contextualInfoButtonId?: string;
};
export const ChartHeader = ({
  contextualInfoButtonId,
  title,
  description
}: ChartHeaderProps) => {
  const wrapContextualInfoButton = (children: React.ReactNode) => {
    if (contextualInfoButtonId) {
      return <ContextualInfoButtonWithText id={contextualInfoButtonId}>{children}</ContextualInfoButtonWithText>;
    }
    return children;
  };
  return <>
      {wrapContextualInfoButton(<h3 className="mb-1 text-base font-semibold text-gray-700 @2xl/pointLevelWrapper:!text-lg @2xl/wrapper:text-xl">
          {title}
        </h3>)}

      <p className="text-sm text-gray-500 @2xl/wrapper:text-base">{description}</p>
    </>;
};

/**
 * Direction of travel for the metric itself: down is always better, in every column. The signs on
 * absolute change and relative change run opposite each other, so the arrow is the stable read.
 *
 * `a` and `b` must be in the same display units as the number beside them — a percent metric
 * already multiplied by 100 — or the zero floor is applied at the wrong scale.
 */
export const getDeltaIcon = (a: number, b: number, unit: "%" | "$" = "%") => {
  // Same tolerance the relative change uses, so the arrow can't claim a change the number denies.
  if (isEffectivelyUnchanged(a, b, unit)) {
    return <EqualsIcon className="text-xs" />;
  }
  return a < b ? <ArrowUpOutlined className="text-xs" data-sentry-element="ArrowUpOutlined" data-sentry-component="getDeltaIcon" data-sentry-source-file="Text.tsx" /> : <ArrowDownOutlined className="text-xs" data-sentry-element="ArrowDownOutlined" data-sentry-component="getDeltaIcon" data-sentry-source-file="Text.tsx" />;
};
type DeltaValueProps = {
  mode: "absoluteChange" | "relativeChange";
  baseline: number | null;
  compare: number | null;
} & ({
  metric: MetricKey;
  unit?: never;
} | {
  unit: "%" | "$";
  metric?: never;
});

/**
 * deltas displayed as unsigned, delta icon shows +/-ve change
 */
export const DeltaValue = ({
  mode,
  baseline,
  compare,
  metric,
  unit
}: DeltaValueProps) => {
  const resolvedUnit: "%" | "$" = metric ? unitForMetric(metric) : unit ?? "%";
  if (baseline === null || compare === null) return <>-</>;
  const value = mode === "absoluteChange" ? computeAbsoluteChange(baseline, compare) : computeRelativeChange(baseline, compare, resolvedUnit);

  // if relative change is null or miniscule(less than 4(%) or 2($) decimal places), return empty
  if (value === null) return <>-</>;
  return <>
      {getDeltaIcon(baseline, compare, resolvedUnit)}
      {mode === "absoluteChange" ? formatAbsoluteChange(value, resolvedUnit) :
    // Relative change is always % including for $ metric
    formatAbbreviatedValueWithMetricUnit(Math.abs(value), "%")}
    </>;
};